/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Card, CardContent, Grid } from "@mui/material";
import { Clear, ListAlt, Search } from "@mui/icons-material";
import { GridActionsCellItem, GridRowId, GridRowModel } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";

import AutoComplete from "../../../components/hookForms/autoComplete";
import InputNumber from "../../../components/hookForms/inputNumber";
import TextField from "../../../components/hookForms/textField";
import { Container } from "../../../theme/globalStyles";
import DataTable from "../../../components/dataTable";
import DialogDescontos from "./modal/dialogDescontos";
import ICampanhaComercial from "../../../interfaces/ICampanhaComercial";
import Tooltip from "@mui/material/Tooltip";
import serviceCampanhaComercial from "../../../services/serviceCampanhaComercial";
import { useAuth } from "../../../context/global";
import { useForm } from "react-hook-form";
import useThrowAsyncError from "../../../hooks/useThrowAsyncError";

const ativoOptions = [
    { label: "Todos", value: "" },
    { label: "Sim", value: "S" },
    { label: "Não", value: "N" },
];

function formatDate(date: string | null): string {
    if (!date) return "-";
    try {
        return new Date(date).toLocaleDateString("pt-BR");
    } catch {
        return "-";
    }
}


const CampanhasComerciais = () => {
    const { setTitlePage } = useAuth();
    const [data, setData] = useState<ICampanhaComercial[]>([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setLoading] = useState(false);
    const [filters, setFilters] = useState<any>(null);

    const [descontosState, setDescontosState] = useState<{
        open: boolean;
        numeroCampanha: number | null;
        nomeCampanha: string | null;
        descontos: any[];
    }>({ open: false, numeroCampanha: null, nomeCampanha: null, descontos: [] });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { ativo: "", numeroCampanha: "", nomeCampanha: "" },
    });

    useEffect(() => {
        setTitlePage("Cadastro - Campanhas Comerciais");
    }, [setTitlePage]);

    useEffect(() => {
        fetchData(filters, 0, pageSize);
    }, []);

    const throwAsyncError = useThrowAsyncError();

    const fetchData = (currentFilters: any, currentPage: number, currentPageSize: number) => {
        setData([]);
        setLoading(true);
        serviceCampanhaComercial
            .getAllCampanhas({ ...(currentFilters ?? {}), page: currentPage, pageSize: currentPageSize })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data?.items ?? []);
                setRowCount(result.data?.rowCount ?? 0);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onSubmit = (values: any) => {
        const newFilters = { ativo: values.ativo ?? "", numeroCampanha: values.numeroCampanha ?? "", nomeCampanha: values.nomeCampanha ?? "" };
        setFilters(newFilters);
        setPage(0);
        fetchData(newFilters, 0, pageSize);
    };

    const onClear = () => {
        reset({ ativo: "", numeroCampanha: "", nomeCampanha: "" });
        setFilters(null);
        setPage(0);
        fetchData(null, 0, pageSize);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        fetchData(filters, newPage, pageSize);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(0);
        fetchData(filters, 0, newPageSize);
    };

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                width: 130,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Ver descontos adicionais">
                                <ListAlt />
                            </Tooltip>
                        }
                        label="Ver descontos adicionais"
                        onClick={() =>
                            setDescontosState({
                                open: true,
                                numeroCampanha: params.row.numeroCampanha,
                                nomeCampanha: params.row.nomeCampanha,
                                descontos: params.row.listaDescontoAdicionalCampanha ?? [],
                            })
                        }
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "Nº Campanha",
                field: "numeroCampanha",
                type: "number",
                headerAlign: "left",
                width: 120,
                sortable: false,
            },
            {
                headerName: "Nome",
                field: "nomeCampanha",
                type: "string",
                flex: 2,
                minWidth: 350,
                sortable: false,
            },
            {
                headerName: "Ativo",
                field: "ativo",
                type: "string",
                width: 80,
                sortable: false,
                valueFormatter: ({ value }: any) => (value === "S" ? "Sim" : value === "N" ? "Não" : "-"),
            },
            {
                headerName: "Deságio (%)",
                field: "desagio",
                type: "number",
                headerAlign: "left",
                width: 110,
                sortable: false,
            },
            {
                headerName: "Bonificação",
                field: "bonificacao",
                type: "string",
                width: 100,
                sortable: false,
                valueFormatter: ({ value }: any) => (value === "S" ? "Sim" : value === "N" ? "Não" : "-"),
            },
            {
                headerName: "Tipo Bonificação",
                field: "tipoBonificacao",
                type: "string",
                width: 150,
                sortable: false,
            },
            {
                headerName: "Percentual Bonificado (%)",
                field: "percentualBonificado",
                type: "number",
                headerAlign: "left",
                width: 180,
                sortable: false,
            },
            {
                headerName: "Início Bonificação",
                field: "inicioBonificacao",
                type: "number",
                headerAlign: "left",
                width: 150,
                sortable: false,
            },
            {
                headerName: "Prazo Pós Vigência",
                field: "prazoPosVigencia",
                type: "number",
                headerAlign: "left",
                width: 160,
                sortable: false,
            },
            {
                headerName: "Início Vigência",
                field: "dataInicioVigencia",
                type: "string",
                width: 130,
                sortable: false,
                valueFormatter: ({ value }: any) => formatDate(value),
            },
            {
                headerName: "Fim Vigência",
                field: "dataFimVigencia",
                type: "string",
                width: 120,
                sortable: false,
                valueFormatter: ({ value }: any) => formatDate(value),
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item md={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2}>
                                            <InputNumber
                                                name="numeroCampanha"
                                                label="Nº Campanha"
                                                control={control}
                                                error={errors}
                                                decimalScale={0}
                                                noThousandSeparator
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                name="nomeCampanha"
                                                label="Nome da Campanha"
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={2}>
                                            <AutoComplete
                                                name="ativo"
                                                label="Ativo"
                                                options={ativoOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item md={2}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                                disabled={isLoading}
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>

                                        <Grid item md={2}>
                                            <Button
                                                disableElevation
                                                variant="outlined"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Clear />}
                                                onClick={onClear}
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.campanhaComercialId}
                            isLoading={isLoading}
                            rowCount={rowCount}
                            page={page}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </Box>

                    {descontosState.open && (
                        <DialogDescontos
                            numeroCampanha={descontosState.numeroCampanha}
                            nomeCampanha={descontosState.nomeCampanha}
                            descontos={descontosState.descontos}
                            onClose={() =>
                                setDescontosState({
                                    open: false,
                                    numeroCampanha: null,
                                    nomeCampanha: null,
                                    descontos: [],
                                })
                            }
                        />
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

export default CampanhasComerciais;